<script setup lang="ts">
/**
 * Console-wide search: type part of a page, site or node name and go
 * there. "/" gets to it from anywhere. On a wide screen it is a box in the
 * navbar, as Tencent Cloud's and Aliyun's are, the results dropping below
 * it once something is typed — nothing comes up for an empty box; on a
 * narrow one a button opening the box in a panel.
 *
 * Everything is filtered client-side from one fetch per open. That is
 * deliberate: an install has tens of sites and nodes, not thousands, so a
 * search endpoint would be infrastructure for a problem nobody has, and
 * every keystroke hitting the API would be worse than one load.
 */
import { ref, computed, onMounted, onUnmounted, nextTick, watch } from 'vue'
import { useRouter, type RouteLocationRaw } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { api } from '@/api/client'
import { quietTooltip } from '@/utils/layout'
import { isNarrow } from '@/utils/responsive'

const { t } = useI18n()
const router = useRouter()

interface Hit {
  kind: 'page' | 'site' | 'node'
  key: string
  label: string
  sub: string
  to: RouteLocationRaw
}

const query = ref('')
// The narrow screen's panel, opened by its button.
const open = ref(false)
// The navbar's box has the focus (wide screens). Its results show only
// while it has, and only once something is typed.
const focused = ref(false)
const shown = computed(() => (isNarrow.value ? open.value : focused.value && !!query.value.trim()))
const loading = ref(false)
const records = ref<Hit[]>([])
const inputRef = ref<{ focus?: () => void; blur?: () => void } | null>(null)
// The tooltip keeps out of the panel's way (quietTooltip).
const { visible: tipVisible, change: onTip } = quietTooltip(open)
// The hit Enter goes to, moved by the arrow keys and the pointer.
const active = ref(0)

// The console's own pages: every route with a title and no parameters.
const pages = computed<Hit[]>(() =>
  router
    .getRoutes()
    .filter((r) => r.name && r.meta?.title && !r.path.includes(':') && r.meta.layout !== 'blank')
    .map((r) => ({
      kind: 'page' as const,
      key: `page-${String(r.name)}`,
      label: t(r.meta.title as string),
      sub: r.path,
      to: { name: r.name! },
    })),
)

const hits = computed(() => {
  const q = query.value.trim().toLowerCase()
  const all = [...pages.value, ...records.value]
  if (!q) return []
  return all.filter((h) => h.label.toLowerCase().includes(q)).slice(0, 10)
})
watch(hits, () => (active.value = 0))

// A site's status in words, as the site list says it.
const STATUS: Record<string, string> = {
  active: 'domains.statusActive',
  draft: 'domains.statusDraft',
  disabled: 'domains.statusDisabled',
}

async function loadIndex() {
  if (records.value.length || loading.value) return
  loading.value = true
  try {
    const [d, n] = await Promise.all([
      api.listDomains().catch(() => ({ items: [] })),
      api.listNodes().catch(() => ({ items: [] })),
    ])
    records.value = [
      ...(d.items ?? []).map((x: any) => ({
        kind: 'site' as const,
        key: `site-${x.id}`,
        label: x.name,
        sub: STATUS[x.status] ? t(STATUS[x.status]) : x.status,
        to: { name: 'domain-detail', params: { id: x.id } },
      })),
      ...(n.items ?? []).map((x: any) => ({
        kind: 'node' as const,
        key: `node-${x.id}`,
        label: x.alias || x.name,
        sub: x.public_ipv4 || x.region || '',
        to: { name: 'node-detail', params: { id: x.id } },
      })),
    ]
  } finally {
    loading.value = false
  }
}

// The panel's box takes the focus as it opens and forgets what was typed
// as it closes. The navbar's box keeps its text, as any search box does,
// until a result is picked; the index is read as it takes the focus.
watch(open, (v) => {
  if (v) {
    void loadIndex()
    if (isNarrow.value) nextTick(() => inputRef.value?.focus?.())
  } else if (isNarrow.value) {
    query.value = ''
  }
})
function onFocus() {
  focused.value = true
  void loadIndex()
}

function go(h: Hit) {
  open.value = false
  query.value = ''
  inputRef.value?.blur?.()
  router.push(h.to)
}

function onKeydown(e: KeyboardEvent) {
  // Enter that ends a Chinese word in the IME is not a pick.
  if (e.isComposing) return
  const n = hits.value.length
  if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
    e.preventDefault()
    if (n) active.value = (active.value + (e.key === 'ArrowDown' ? 1 : n - 1)) % n
  } else if (e.key === 'Enter') {
    if (n) go(hits.value[active.value] ?? hits.value[0])
  } else if (e.key === 'Escape') {
    open.value = false
    inputRef.value?.blur?.()
  }
}

const tagColor = { page: 'gray', site: 'arcoblue', node: 'green' } as const
const tagLabel = (k: Hit['kind']) => t(k === 'page' ? 'app.searchPage' : k === 'site' ? 'app.searchSite' : 'app.searchNode')

// "/" goes to the search. Ignored while typing in another field, or the
// key would be swallowed from every search box and text area on the page.
function onKey(e: KeyboardEvent) {
  if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return
  const el = e.target as HTMLElement | null
  const tag = el?.tagName
  if (tag === 'INPUT' || tag === 'TEXTAREA' || el?.isContentEditable) return
  e.preventDefault()
  if (isNarrow.value) open.value = true
  else inputRef.value?.focus?.()
}

onMounted(() => window.addEventListener('keydown', onKey))
onUnmounted(() => window.removeEventListener('keydown', onKey))
</script>

<template>
  <!-- The panel's button opens it with a click; the navbar's box shows its
       results by itself (shown), so it takes no trigger of Arco's. -->
  <a-trigger
    :popup-visible="shown"
    :trigger="isNarrow ? 'click' : []"
    :position="isNarrow ? 'br' : 'bl'"
    :popup-offset="isNarrow ? 10 : 4"
    auto-fit-popup-min-width
    :unmount-on-close="false"
    @popup-visible-change="(v: boolean) => (open = v)"
  >
    <a-tooltip v-if="isNarrow" :content="t('app.search')" :popup-visible="tipVisible" @popup-visible-change="onTip">
      <a-button class="nav-btn" type="text" shape="square">
        <template #icon><icon-search /></template>
      </a-button>
    </a-tooltip>
    <a-input
      v-else
      ref="inputRef"
      v-model="query"
      class="nav-search"
      :placeholder="t('app.searchPlaceholder')"
      allow-clear
      @focus="onFocus"
      @blur="focused = false"
      @keydown="onKeydown"
    >
      <!-- Search with a spark, as Tencent Cloud's box has it; inked with
           the ring's blue to violet while the ring is out. -->
      <template #prefix>
        <svg class="spark-search" viewBox="0 0 24 24" aria-hidden="true">
          <defs>
            <linearGradient id="nav-search-ink" x1="3" y1="21" x2="21" y2="3" gradientUnits="userSpaceOnUse">
              <stop offset="0" stop-color="#2f6bff" />
              <stop offset="1" stop-color="#a35cff" />
            </linearGradient>
          </defs>
          <path class="lens" d="M16.98 9.93A6.5 6.5 0 1 1 11.07 4.03M15.1 15.1L20 20" />
          <path class="spark" d="M18.5 1Q18.5 4.5 22 4.5Q18.5 4.5 18.5 8Q18.5 4.5 15 4.5Q18.5 4.5 18.5 1Z" />
        </svg>
      </template>
    </a-input>
    <template #content>
      <div class="search-panel" :class="{ inline: !isNarrow }">
        <a-input
          v-if="isNarrow"
          ref="inputRef"
          v-model="query"
          :placeholder="t('app.searchPlaceholder')"
          allow-clear
          @keydown="onKeydown"
        >
          <template #prefix><icon-search /></template>
        </a-input>
        <div class="results">
          <div v-if="loading && !hits.length" class="empty">{{ t('common.loading') }}</div>
          <div v-else-if="!hits.length" class="empty">{{ query ? t('app.searchNoHit') : t('app.searchHint') }}</div>
          <div
            v-for="(h, i) in hits"
            :key="h.key"
            class="hit"
            :class="{ active: i === active }"
            @mouseenter="active = i"
            @mousedown.prevent="go(h)"
          >
            <a-tag size="small" :color="tagColor[h.kind]">{{ tagLabel(h.kind) }}</a-tag>
            <span class="hit-label">{{ h.label }}</span>
            <span class="hit-sub">{{ h.sub }}</span>
          </div>
        </div>
      </div>
    </template>
  </a-trigger>
</template>

<style scoped>
/* The navbar's box, Tencent Cloud's: 320px (narrower when the bar is), on
   the bar's own ground inside a hairline, rounded. */
.nav-search.arco-input-wrapper {
  position: relative;
  width: 320px;
  background-color: var(--color-bg-2);
  border-color: var(--color-border-2);
  border-radius: 8px;
}
/* The spark a little closer to the words than Arco's 12px. */
.nav-search :deep(.arco-input-prefix) {
  padding-right: 8px;
}
.nav-search.arco-input-wrapper:hover,
.nav-search.arco-input-wrapper.arco-input-focus {
  background-color: var(--color-bg-2);
  border-color: transparent;
}
/* Under the pointer or with focus, a ring of pastel blue, violet and gold
   runs round it — a conic gradient turning, masked to a 2px band over the
   hairline and the pixel outside it, so nothing in the box moves. Paused
   rather than stopped when it goes, so it fades where it was instead of
   jumping back; still where the reader has asked for less motion. */
.nav-search.arco-input-wrapper::before {
  position: absolute;
  inset: -1px;
  padding: 2px;
  border-radius: 9px;
  background: conic-gradient(from var(--nav-ring-turn), #a8c8ff, #c4b8fb, #f2d39a, #ffe2b8, #b4dcfb, #a8c8ff);
  content: '';
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s;
  animation: nav-ring-run 4s linear infinite paused;
  -webkit-mask:
    linear-gradient(#000 0 0) content-box,
    linear-gradient(#000 0 0);
  -webkit-mask-composite: xor;
  mask:
    linear-gradient(#000 0 0) content-box exclude,
    linear-gradient(#000 0 0);
}
.nav-search.arco-input-wrapper:hover::before,
.nav-search.arco-input-wrapper.arco-input-focus::before {
  opacity: 1;
  animation-play-state: running;
}
@media (prefers-reduced-motion: reduce) {
  .nav-search.arco-input-wrapper::before {
    animation: none;
  }
}
@property --nav-ring-turn {
  syntax: '<angle>';
  inherits: false;
  initial-value: 0deg;
}
@keyframes nav-ring-run {
  to {
    --nav-ring-turn: 360deg;
  }
}
.spark-search {
  width: 16px;
  height: 16px;
  color: var(--color-text-2);
  fill: none;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
}
.spark-search .spark {
  fill: currentColor;
  stroke: none;
}
.nav-search:hover .spark-search .lens,
.nav-search.arco-input-focus .spark-search .lens {
  stroke: url(#nav-search-ink);
}
.nav-search:hover .spark-search .spark,
.nav-search.arco-input-focus .spark-search .spark {
  fill: url(#nav-search-ink);
}
.search-panel {
  width: 360px;
  max-width: calc(100vw - 24px);
  padding: 12px;
  background: var(--color-bg-popup);
  border: 1px solid var(--color-border-2);
  border-radius: var(--border-radius-medium);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
}
/* Under the navbar's box, Arco's dropdown: the box's width, and room for
   a site's name when the bar has narrowed the box. */
.search-panel.inline {
  box-sizing: border-box;
  width: auto;
  min-width: 300px;
  padding: 4px;
}
.search-panel.inline .results {
  margin-top: 0;
}
.results {
  max-height: 320px;
  margin-top: 8px;
  overflow-y: auto;
}
.hit {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 8px;
  border-radius: var(--border-radius-small);
  cursor: pointer;
}
.hit.active {
  background: var(--color-fill-2);
}
.hit-label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  color: var(--color-text-1);
}
.hit-sub {
  flex: none;
  margin-left: auto;
  font-size: 12px;
  color: var(--color-text-3);
}
.empty {
  padding: 16px 8px;
  text-align: center;
  color: var(--color-text-3);
  font-size: 13px;
}
</style>
